import { clamp } from 'lodash-es';
import { forwardRef, useMemo } from 'react';

import { useMergedRef } from '@/hook';

import { VBaseSelect, useVBaseSelectContext } from '../VBaseSelect';
import { VFlex } from '../VFlex';
import { Folder, KeyboardArrowRight, MoreHoriz } from '../asset';
import { joinCn } from '../util';
import style from './style';
import type { VBreadcrumbsProps } from './type';

const VBreadcrumbs = <T extends { [x: string]: any }>({
  pathList,
  value,
  onChange,
  size = 'medium',
  lastVisiblePathNum,
  maxPathWidth,
}: VBreadcrumbsProps<T>) => {
  const lastIndex = pathList.length - 1;

  const clampedLastVisiblePathNum = clamp(
    lastVisiblePathNum ?? lastIndex,
    1,
    lastIndex,
  );

  const visibleItemList = useMemo(() => {
    if (pathList.length <= clampedLastVisiblePathNum + 1) {
      return pathList.map((item, index) => ({
        item,
        index,
        type: 'item' as const,
      }));
    }

    const items = [];

    items.push({ item: pathList[0], index: 0, type: 'item' as const });
    lastIndex > clampedLastVisiblePathNum &&
      items.push({ type: 'ellipsis' as const });

    const lastItems = pathList
      .slice(-clampedLastVisiblePathNum)
      .map((item, i) => ({
        item,
        index: lastIndex - clampedLastVisiblePathNum + 1 + i,
        type: 'item' as const,
      }));

    items.push(...lastItems);

    return items;
  }, [pathList, clampedLastVisiblePathNum, lastIndex]);

  const ellipsisItemList = useMemo(() => {
    return pathList.slice(1, clampedLastVisiblePathNum + 1);
  }, [pathList, clampedLastVisiblePathNum]);

  return (
    <VFlex className="flex-wrap" items="center" justify="center" gap={0.25}>
      {visibleItemList.map((item, i) => {
        if (item.type === 'ellipsis') {
          return (
            <VFlex
              key={`ellipsis_${i}`}
              className={style.vBreadcrumbsPathItem(false, size, false)}
              gap={0.25}
              items="center"
            >
              <VBaseSelect
                itemList={ellipsisItemList.map(item => ({
                  label: item.label,
                  value: item.value,
                  disabled: item.disabled,
                }))}
                renderItem={({ item }) => {
                  return (
                    <>
                      <Folder
                        className={joinCn('size-5', {
                          'text-[#50C7FF]': !item.disabled,
                          'text-gray-300': item.disabled,
                        })}
                      />
                      {item.label}
                    </>
                  );
                }}
                onItemClick={e => {
                  if (e.target.item.disabled) return;

                  const selectedValue = e.target.item.value;
                  const selectedItem = pathList.find(
                    item => item.value === selectedValue,
                  );
                  const selectedIndex = pathList.findIndex(
                    item => item.value === selectedValue,
                  );
                  if (selectedItem) {
                    onChange?.(selectedItem, selectedIndex);
                  }
                }}
              >
                <VInnerEllipsis />
              </VBaseSelect>
              <div className={style.vBreadcrumbsPathItemIcon(false)}>
                <KeyboardArrowRight strokeWidth={0.5} />
              </div>
            </VFlex>
          );
        }

        const isActive = value === item.item.value;
        const isLast = i === visibleItemList.length - 1;
        const disabled = !!item.item.disabled;

        return (
          <VFlex
            role="link"
            key={`${item.item.value}_${item.index}`}
            className={style.vBreadcrumbsPathItem(isActive, size, disabled)}
            gap={0.25}
            items="center"
            onClick={() => {
              if (disabled) return;
              onChange?.(item.item, item.index);
            }}
          >
            <span
              style={
                {
                  '--max-width': maxPathWidth ? `${maxPathWidth}px` : 'unset',
                } as React.CSSProperties
              }
              className={joinCn(
                maxPathWidth
                  ? `max-w-[var(--max-width)] overflow-hidden text-ellipsis whitespace-nowrap`
                  : '',
              )}
            >
              {item.item.label}
            </span>
            {!isLast && (
              <div className={style.vBreadcrumbsPathItemIcon(isActive)}>
                <KeyboardArrowRight strokeWidth={0.5} />
              </div>
            )}
          </VFlex>
        );
      })}
    </VFlex>
  );
};

const VInnerEllipsis = forwardRef((_, ref) => {
  const { $displayValue, itemListVisible } = useVBaseSelectContext();
  const refs = useMergedRef($displayValue, ref);

  return (
    <MoreHoriz
      ref={refs}
      className={style.vBreadcrumbsEllipsis(itemListVisible)}
    />
  );
});

export default VBreadcrumbs;
